/*
  Kiwi Ears · Light Bloom WebGL background (Rene 2026-08-22).

  Ported from the Originkit "Light Bloom" React component supplied by Rene. Both
  shaders are carried over verbatim — the bloom falloff and its normalisation
  against the origin depth, the shaft noise, the accent ramp at 0.58–0.99, the
  grain and the vignette are byte-for-byte the original. The DPR cap of 2, the
  clock wrap at 3600 and the dt-correct pointer smoothing are the component's
  own decisions and are kept.

  What changed, because this theme is Liquid + plain JS rather than React:

   · props → data-* attributes, so a section can mount one from Liquid.
   · the refs that existed only to keep an empty-dep effect stable are gone;
     values are read once at mount.
   · PREFERS-REDUCED-MOTION: the original always animates. Here such a visitor
     gets one frame and no loop, and the pointer is not tracked at all.
   · IntersectionObserver + visibilitychange pause the loop when the section is
     off screen or the tab is hidden. A full-screen fragment shader running
     behind a section nobody is looking at is the most expensive thing on the
     page; the original had no such gate.
   · the root's minWidth/minHeight (1200×800) are dropped — they existed to stop
     a Framer "Fit Content" frame collapsing. Here the host is absolutely
     positioned inside a section that already has height.
   · context loss is handled: the loop stops rather than spinning on a dead
     context.
   · the hover listeners move from the canvas to its parent section, since a
     background layer must not take pointer events.

  Markup contract — the host must be sized by its own CSS:

    <div data-lightbloom
         data-variant="shafts" data-direction="bottom"
         data-background="#171724" data-base="#3C158F" data-accent="#8800FF"
         data-speed="77" data-hover="156"
         data-rise="87" data-spread="49"
         data-shaft-count="16" data-shaft-amount="37" data-shaft-drift="70"
         data-grain="12" data-vignette="25">
      <canvas></canvas>
    </div>

  Dial values are the component's 0–100 scale (speed is /50, the rest /100).
*/
(function () {
  var MAX_DPR = 2;

  var VERT_SRC = [
    'attribute vec2 a_pos;',
    'void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }'
  ].join('\n');

  var FRAG_SRC = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    '',
    'uniform vec2  uRes;',
    'uniform float uTime;',
    'uniform int   uStyle;',
    'uniform int   uDirection;',
    'uniform vec3  uBg;',
    'uniform vec3  uBase;',
    'uniform vec3  uAccent;',
    'uniform float uRise;',
    'uniform float uSpread;',
    'uniform float uOriginX;',
    'uniform float uLift;',
    'uniform float uShaftCount;',
    'uniform float uShaftAmount;',
    'uniform float uShaftDrift;',
    'uniform float uGrain;',
    'uniform float uVignette;',
    '',
    'float h11(float x) { return fract(sin(x * 127.1) * 43758.5453123); }',
    '',
    'float vn1(float x) {',
    '    float i = floor(x);',
    '    float f = fract(x);',
    '    f = f * f * (3.0 - 2.0 * f);',
    '    return mix(h11(i), h11(i + 1.0), f);',
    '}',
    '',
    'void main() {',
    '    vec2 uv = gl_FragCoord.xy / uRes;',
    '    float aspect = uRes.x / uRes.y;',
    '    float t = uTime;',
    '',
    '    float breathe = 1.0 + 0.06 * sin(t * 0.35);',
    '',
    '    float depth = mix(0.05, 1.20, uSpread);',
    '    vec2 lp;',
    '    float acrossUv;',
    '    float edgeCoord;',
    '    if (uDirection == 1) {',
    '        lp = vec2(uOriginX, 1.0 + depth);',
    '        acrossUv = uv.x;',
    '        edgeCoord = 1.0 - uv.y;',
    '    } else if (uDirection == 2) {',
    '        lp = vec2(-depth / aspect, uOriginX);',
    '        acrossUv = uv.y;',
    '        edgeCoord = uv.x;',
    '    } else if (uDirection == 3) {',
    '        lp = vec2(1.0 + depth / aspect, uOriginX);',
    '        acrossUv = uv.y;',
    '        edgeCoord = 1.0 - uv.x;',
    '    } else {',
    '        lp = vec2(uOriginX, -depth);',
    '        acrossUv = uv.x;',
    '        edgeCoord = uv.y;',
    '    }',
    '    vec2 q = vec2((uv.x - lp.x) * aspect, uv.y - lp.y);',
    '    float d = length(q);',
    '',
    '    float k = mix(9.0, 1.4, uRise) / breathe;',
    '    float g = exp(-d * k) / max(exp(-depth * k), 1e-4);',
    '    g = clamp(g, 0.0, 1.0);',
    '',
    '    if (uStyle == 1) {',
    '        float sx = acrossUv * uShaftCount;',
    '        float dr = t * uShaftDrift;',
    '        float s = vn1(sx + dr) * 0.6 + vn1(sx * 2.17 - dr * 0.8) * 0.4;',
    '        float mask = smoothstep(0.0, 0.35, edgeCoord);',
    '        g *= mix(1.0, 0.45 + 1.25 * s, uShaftAmount * mask);',
    '    }',
    '',
    '    g = clamp(g * (1.0 + uLift * 0.25), 0.0, 1.0);',
    '',
    '    vec3 col = mix(uBg, uBase, smoothstep(0.0, 0.68, g));',
    '    col = mix(col, uAccent, smoothstep(0.58, 0.99, g));',
    '',
    '    vec2 vc = uv - 0.5;',
    '    vc.x *= aspect;',
    '    col *= 1.0 - uVignette * smoothstep(0.35, 0.95, length(vc));',
    '',
    '    float rnd = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);',
    '    col += (rnd - 0.5) * (uGrain * 0.06 + 1.5 / 255.0);',
    '',
    '    gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);',
    '}'
  ].join('\n');

  function parseColor(input, fb) {
    if (!input) return fb;
    var str = String(input).trim();
    if (str.charAt(0) === '#') {
      var hex = str.slice(1);
      if (hex.length === 3 || hex.length === 4) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      if (hex.length >= 6) {
        var r = parseInt(hex.slice(0, 2), 16);
        var g = parseInt(hex.slice(2, 4), 16);
        var b = parseInt(hex.slice(4, 6), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) return [r / 255, g / 255, b / 255];
      }
      return fb;
    }
    var m = str.match(/[\d.]+/g);
    if (m && m.length >= 3) {
      return [
        Math.min(255, parseFloat(m[0])) / 255,
        Math.min(255, parseFloat(m[1])) / 255,
        Math.min(255, parseFloat(m[2])) / 255
      ];
    }
    return fb;
  }

  function dial(host, key, fb) {
    var v = parseFloat(host.getAttribute('data-' + key));
    return isFinite(v) ? v : fb;
  }

  function compile(gl, type, src) {
    var sh = gl.createShader(type);
    if (!sh) return null;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      if (window.console) console.error('LightBloom shader:', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function mount(host) {
    var canvas = host.querySelector('canvas');
    if (!canvas) return;
    var gl = canvas.getContext('webgl', { antialias: false, alpha: false }) ||
             canvas.getContext('experimental-webgl', { antialias: false, alpha: false });
    if (!gl) return;                 /* the section's CSS background stands in */

    var vs = compile(gl, gl.VERTEX_SHADER, VERT_SRC);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
    if (!vs || !fs) return;
    var prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      if (window.console) console.error('LightBloom link:', gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var posLoc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    function loc(n) { return gl.getUniformLocation(prog, n); }
    var u = {
      res: loc('uRes'), time: loc('uTime'), style: loc('uStyle'), direction: loc('uDirection'),
      bg: loc('uBg'), base: loc('uBase'), accent: loc('uAccent'),
      rise: loc('uRise'), spread: loc('uSpread'), originX: loc('uOriginX'), lift: loc('uLift'),
      count: loc('uShaftCount'), amount: loc('uShaftAmount'), drift: loc('uShaftDrift'),
      grain: loc('uGrain'), vignette: loc('uVignette')
    };

    var dir = host.getAttribute('data-direction');
    var v = {
      style: host.getAttribute('data-variant') === 'bloom' ? 0 : 1,
      direction: dir === 'top' ? 1 : dir === 'left' ? 2 : dir === 'right' ? 3 : 0,
      bg: parseColor(host.getAttribute('data-background'), [0.09, 0.09, 0.14]),
      base: parseColor(host.getAttribute('data-base'), [0.235, 0.082, 0.561]),
      accent: parseColor(host.getAttribute('data-accent'), [0.533, 0, 1]),
      rise: dial(host, 'rise', 87) / 100,
      spread: dial(host, 'spread', 49) / 100,
      speed: dial(host, 'speed', 77) / 50,
      hover: dial(host, 'hover', 156) / 100,
      count: Math.max(1, dial(host, 'shaft-count', 16)),
      amount: dial(host, 'shaft-amount', 37) / 100,
      drift: dial(host, 'shaft-drift', 70) / 100,
      grain: dial(host, 'grain', 12) / 100,
      vignette: dial(host, 'vignette', 25) / 100
    };

    var still = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var ptr = { x: 0.5, target: 0.5, on: 0, onTarget: 0 };
    var st = { isVisible: true, isPageVisible: true, raf: 0, lost: false };
    var last = 0, clock = 0;

    function frame(dt) {
      var vv = v;
      clock = (clock + dt * vv.speed) % 3600;

      var dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      var cw = host.clientWidth || canvas.clientWidth || 1200;
      var ch = host.clientHeight || canvas.clientHeight || 800;
      var bw = Math.max(1, Math.round(cw * dpr));
      var bh = Math.max(1, Math.round(ch * dpr));
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
        gl.viewport(0, 0, bw, bh);
      }

      var kx = 1 - Math.exp(-8 * dt);
      var ko = 1 - Math.exp(-5 * dt);
      ptr.x += (ptr.target - ptr.x) * kx;
      ptr.on += (ptr.onTarget - ptr.on) * ko;

      var travel = Math.min(1, vv.hover);
      var originX = 0.5 + (ptr.x - 0.5) * travel * ptr.on;

      gl.uniform2f(u.res, bw, bh);
      gl.uniform1f(u.time, clock);
      gl.uniform1i(u.style, vv.style);
      gl.uniform1i(u.direction, vv.direction);
      gl.uniform3f(u.bg, vv.bg[0], vv.bg[1], vv.bg[2]);
      gl.uniform3f(u.base, vv.base[0], vv.base[1], vv.base[2]);
      gl.uniform3f(u.accent, vv.accent[0], vv.accent[1], vv.accent[2]);
      gl.uniform1f(u.rise, vv.rise);
      gl.uniform1f(u.spread, vv.spread);
      gl.uniform1f(u.originX, originX);
      gl.uniform1f(u.lift, vv.hover * ptr.on);
      gl.uniform1f(u.count, vv.count);
      gl.uniform1f(u.amount, vv.amount);
      gl.uniform1f(u.drift, vv.drift);
      gl.uniform1f(u.grain, vv.grain);
      gl.uniform1f(u.vignette, vv.vignette);

      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function loop(now) {
      var dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      frame(dt);
      st.raf = requestAnimationFrame(loop);
    }

    function start() {
      if (still || st.lost || st.raf || !st.isVisible || !st.isPageVisible) return;
      last = performance.now();
      st.raf = requestAnimationFrame(loop);
    }
    function stop() {
      if (st.raf) { cancelAnimationFrame(st.raf); st.raf = 0; }
    }

    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      st.lost = true;
      stop();
    });

    if (still) {                     /* one frame, then nothing */
      frame(0);
      window.addEventListener('resize', function () { frame(0); }, { passive: true });
      return;
    }

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        st.isVisible = entries[0] ? entries[0].isIntersecting : true;
        if (st.isVisible && st.isPageVisible) start(); else stop();
      }, { threshold: 0 }).observe(host);
    }
    document.addEventListener('visibilitychange', function () {
      st.isPageVisible = document.visibilityState === 'visible';
      if (st.isVisible && st.isPageVisible) start(); else stop();
    });

    /* The original listens on the canvas. Here the canvas is a background layer
       with pointer-events:none — it would never see an event, and giving it
       events back would mean the lift died whenever the cursor crossed the copy.
       So the SECTION is the hover surface; coordinates still come off the canvas
       rect, exactly as before. */
    var surface = host.parentElement || host;
    surface.addEventListener('pointermove', function (e) {
      var r = canvas.getBoundingClientRect();
      if (v.direction === 2 || v.direction === 3) {
        var h = canvas.offsetHeight || 1;
        var sy = r.height > 0 ? h / r.height : 1;
        ptr.target = Math.max(0, Math.min(1, 1 - ((e.clientY - r.top) * sy) / h));
      } else {
        var w = canvas.offsetWidth || 1;
        var sx = r.width > 0 ? w / r.width : 1;
        ptr.target = Math.max(0, Math.min(1, ((e.clientX - r.left) * sx) / w));
      }
    });
    surface.addEventListener('pointerenter', function () { ptr.onTarget = 1; });
    surface.addEventListener('pointerleave', function () { ptr.onTarget = 0; });

    start();
  }

  function init() {
    [].slice.call(document.querySelectorAll('[data-lightbloom]')).forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
